import axios, { AxiosError } from "axios";
import { getAccessToken, getRefreshToken, setAccessToken, setRefreshToken } from "../utils/jwt";
import { refreshTokenService } from "./api/auth";
import { logout } from "./authService";
import { toast } from "react-toastify";
import { Errors } from "../types/enums/Errors";

export interface ApiRequestOptions<I = any> extends RequestInit {
    skipAuth?: boolean;
    input?: I;
}

function isTokenExpired(token: string): boolean {
    try {
        const [, payload] = token.split(".");
        const { exp } = JSON.parse(atob(payload));
        return exp * 1000 < Date.now();
    } catch {
        return true;
    }
}

async function refreshTokenIfNeeded() {
    const token = getAccessToken();
    if (!token || isTokenExpired(token)) {
        try {
            const refresh = getRefreshToken();
            if (!refresh) throw new Error("No refresh token");

            const { accessToken, refreshToken } = await refreshTokenService(
                refresh
            );

            setAccessToken(accessToken);
            setRefreshToken(refreshToken);
            return accessToken;
        } catch (e) {
            logout();
        }
    }
    return token;
}

// Using axios for API requests
async function apiRequest<I = any>(
    route: string,
    options: ApiRequestOptions<I> = {}
): Promise<Response> {
    // Refresh token if needed
    let accessToken = options.skipAuth
        ? undefined
        : await refreshTokenIfNeeded();

    // Convert Headers instance to plain object if needed
    let headers: Record<string, string> = {
        "Content-Type": "application/json",
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    };

    if (options.headers) {
        if (options.headers instanceof Headers) {
            options.headers.forEach((value, key) => {
                headers[key] = value;
            });
        } else {
            headers = {
                ...headers,
                ...(options.headers as Record<string, string>),
            };
        }
    }

    let axiosConfig: any = {};
    if (options.input) {
        let key = "data";

        if (!options.method || options.method === "GET") {
            key = "params";
        }

        axiosConfig[key] = options.input;
    }

    const response = await axios({
        url: `${process.env.REACT_APP_JT_API}${route}`,
        method: (options.method as any) || "GET",
        headers,
        ...axiosConfig,
    });

    return response.data;
}

// Fonction wrapper pour gérer les erreurs globales
export const request = async <T = any, I = any>(
    route: string,
    options: ApiRequestOptions<I> = {}
): Promise<T | undefined> => {
    try {
        const response = await apiRequest<I>(route, options);
        return response as T;
    } catch (error) {
        if (
            error instanceof AxiosError &&
            error.response &&
            error.response.status === 403
        ) {
            // Tentative de renouvellement du token
            try {
                await refreshTokenIfNeeded();
                const response = await apiRequest(route, options);
                return response as T;
            } catch (retryError) {
                logout();
                toast.error(
                    "Une erreur s'est produite, veuillez réessayer plus tard"
                );
            }
        } else if (
            error instanceof AxiosError &&
            error.response &&
            error.response.status === 404
        ) {
            // Ressource non trouvée
            return Promise.reject();
        } else if (
            error instanceof AxiosError &&
            error.response?.status === 409 &&
            (error.response.data as { code?: string } | undefined)?.code ===
                "REPEAT_CONFIRMATION_REQUIRED"
        ) {
            return Promise.reject(error);
        } else if (
            error instanceof AxiosError &&
            error.response?.status === 400
        ) {
            const data = error.response.data as { error?: string };
            if (data?.error === "SESSION_DELETE_WINDOW_EXPIRED") {
                toast.error(Errors.SESSION_DELETE_WINDOW_EXPIRED);
                return Promise.reject(error);
            }
        }

        // Erreur générique (ne pas invalider la session / déconnecter)
        if (error instanceof AxiosError && error.response) {
            const data = error.response.data as { error?: string };
            const errorMessage =
                data?.error && data.error in Errors
                    ? Errors[data.error as keyof typeof Errors]
                    : Errors.GENERIC;
            toast.error(errorMessage);
        }

        return Promise.reject(error);
    }
};
